import { Link } from "react-router-dom";
import "@/App.css";
import { 
  ShieldAlert, 
  MapPin,
  Phone,
  DollarSign,
  CheckCircle,
  ChevronRight,
  Briefcase,
  Clock,
  Globe,
  ArrowLeft,
  FileText,
  Users,
  Calendar,
  Plane
} from "lucide-react";

function JobPhilippines() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm border-b border-white/10">
        <div className="section-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
            <span 
              className="font-bold text-xl tracking-tight uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="logo-text"
            >
              Simon Advanced Security
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="footer-link hover:text-orange-500" data-testid="nav-home">
              Home
            </Link>
            <Link to="/jobs" className="footer-link hover:text-orange-500" data-testid="nav-jobs">
              All Jobs
            </Link>
            <a 
              href="tel:+18008241906" 
              className="btn-primary text-sm py-2 px-4"
              data-testid="nav-call-btn"
            >
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Back Link */}
      <div className="pt-24 pb-4 bg-[#0A0A0A]">
        <div className="section-container">
          <Link 
            to="/jobs" 
            className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors text-sm"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Jobs
          </Link>
        </div>
      </div>

      {/* Job Header */}
      <section className="py-8 bg-[#0A0A0A] relative overflow-hidden" data-testid="job-header">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px),
                              repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px)`
          }} />
        </div>
        
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-green-500/20 border border-green-500/50 text-green-400 text-xs px-3 py-1 uppercase tracking-wider">
                Long-Term
              </span>
              <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-xs px-3 py-1 uppercase tracking-wider">
                Executive Protection
              </span>
            </div>
            
            <h1 
              className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="job-title"
            >
              Executive Protection Detail – Manila, Philippines
            </h1>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">Manila, Philippines</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">$400 - $700/day + Expenses</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">2-4 Month Rotation</span>
              </div>
            </div>

            <a 
              href="tel:+18008241906"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="apply-btn-top"
            >
              <Phone className="w-5 h-5" />
              Apply Now: 800-824-1906
            </a>
          </div>
        </div>
      </section>

      {/* Job Content */}
      <section className="py-16 bg-[#121212]">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div data-testid="job-overview">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Position Overview
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-400 text-base leading-relaxed mb-4">
                    Simon Advanced Security is seeking several large-scale <strong className="text-white">Executive Protection details</strong> (comprised of both male and female agents) for Full-Time Contract Employment within <strong className="text-white">Manila, Philippines</strong>. Occasional domestic and international travel will be required as well.
                  </p>
                  <div className="bg-green-500/10 border border-green-500/30 p-4 my-6">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-300 text-sm">
                        <strong className="text-green-400">Long-Term Opportunity:</strong> This is a long-term client need with flexible rotating schedules. Initial rotation requires 2-4 months of commitment.
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-base leading-relaxed">
                    Only highly experienced candidates who possess at least 5+ years of military and/or law enforcement and experience in protective operations will be considered for this role.
                  </p>
                </div>
              </div>

              {/* Duties & Responsibilities */}
              <div data-testid="job-duties">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Duties & Responsibilities
                </h2>
                <ul className="space-y-4">
                  {[
                    "Perform mobile and static low-profile / covert protection and counter surveillance",
                    "Conduct advance work and report on gathered intelligence",
                    "Conduct vehicle checks to include service, wash, and fuel",
                    "Liaise with local authorities when applicable emergency situations arise",
                    "Practice OPSEC and COMSEC with sensitive and confidential material",
                    "Flex and adapt as needed based on client needs",
                    "Operate as part of a multi-faceted team",
                    "Travel in support of protective operations, both domestically and internationally",
                    "Work weekends, late hours and holidays"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Minimum Qualifications */}
              <div data-testid="job-requirements">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Minimum Qualifications
                </h2>
                <p className="text-red-400 text-sm mb-6 uppercase tracking-wider">
                  Do not apply without meeting these minimum qualification requirements
                </p>
                <ul className="space-y-4">
                  {[
                    { text: "Must have 5+ years combined experience from either military and/or law enforcement as well as experience in protective operations", critical: true },
                    { text: "Have a strong working knowledge and experience in close protection operations", critical: true },
                    { text: "Have strong working knowledge of technology-based protective security applications", critical: false },
                    { text: "Physically fit and maintain a clean and sharp appearance", critical: false },
                    { text: "Pass a background check (no misdemeanors, felonies or other criminal charges)", critical: true },
                    { text: "Pass pre-employment drug screening and periodic, random drug screenings throughout employment", critical: false },
                    { text: "Possess a high-degree of professionalism, responsibility and maturity", critical: false },
                    { text: "Must be able to brief, persuade, and instruct others on security requirements and policies", critical: false },
                    { text: "Possess a current US Passport", critical: true },
                    { text: "Must have a valid US driver's license with a clean driving record", critical: false }
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${item.critical ? 'text-red-500' : 'text-orange-500'}`} strokeWidth={1.5} />
                      <span className={`text-sm ${item.critical ? 'text-white font-medium' : 'text-gray-300'}`}>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Preferred Qualifications */}
              <div data-testid="job-preferred">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Preferred Qualifications
                </h2>
                <ul className="space-y-4">
                  {[
                    "Candidates who have experience providing executive protection for ultra high net worth individuals (UHNWI)",
                    "Candidates with prior backgrounds in a SOF or combat arms MOS are highly desired",
                    "Have prior experience conducting surveillance / counter surveillance operations within the private security, private military, or military sectors",
                    "Have prior experience conducting advance operations in support of private security protection details",
                    "Possess prior experience as a security driver – specifically driving a client as part of a private security detail or PSD team",
                    "Prior experience working with FBOs",
                    "Prior attendance of a recognized Executive Protection Course (i.e. ESI, EPI, Oatman, PFC, ITG4, TFTT)",
                    "Attended an industry-recognized vehicle dynamics or security driving course (i.e. Summit Point BSR, Tony Scotti, ESI, EPI, O'Gara, Blackwater, etc.)",
                    "Possess a current travel or work visa for the Philippines",
                    "Have prior security experience in the Philippines"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-500/60 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-400 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Required Skills */}
              <div data-testid="job-skills">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Required Skills
                </h2>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Executive Protection",
                    "Operations",
                    "PSD - Foreign",
                    "Close Protection",
                    "Counter Surveillance"
                  ].map((skill, index) => (
                    <span 
                      key={index}
                      className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-3 py-2 uppercase tracking-wider"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Apply Card */}
                <div className="bg-black/40 border border-white/10 p-6" data-testid="apply-card">
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-4"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Apply For This Position
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Compensation</p>
                        <p className="text-white text-lg font-bold">$400 - $700/day</p>
                        <p className="text-orange-400 text-xs">+ Expenses</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Location</p>
                        <p className="text-white">Manila, Philippines</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Employment Type</p>
                        <p className="text-white">Full-Time Contractor</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Initial Rotation</p>
                        <p className="text-white">2-4 Months</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Start Date</p>
                        <p className="text-white">Immediate</p>
                      </div>
                    </div>
                  </div>
                  <a 
                    href="tel:+18008241906"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="apply-btn-sidebar"
                  >
                    <Phone className="w-5 h-5" />
                    800-824-1906
                  </a>
                </div>

                {/* Long-term Notice */}
                <div className="bg-green-500/10 border border-green-500/30 p-6">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold text-sm mb-2">Long-Term Client Need</h4>
                      <p className="text-gray-400 text-xs">
                        This is a stable, long-term position with flexible rotating schedules and opportunity for continued employment.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Travel Notice */}
                <div className="bg-orange-500/10 border border-orange-500/30 p-6">
                  <div className="flex items-start gap-3">
                    <Plane className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold text-sm mb-2">Travel Required</h4>
                      <p className="text-gray-400 text-xs">
                        Occasional domestic and international travel will be required in support of protective operations.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Other Jobs Link */}
                <Link 
                  to="/jobs"
                  className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-colors"
                  data-testid="other-jobs-link"
                >
                  <h4 className="text-white font-bold text-sm mb-2">View Other Positions</h4>
                  <p className="text-gray-400 text-xs">
                    Explore more high-threat security positions worldwide.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span 
                className="font-bold text-lg tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Simon Advanced Security
              </span>
            </Link>
            <p className="text-gray-500 text-xs tracking-widest">
              © {new Date().getFullYear()} ALL RIGHTS RESERVED
            </p>
            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link" data-testid="footer-home">Home</Link>
              <Link to="/jobs" className="footer-link" data-testid="footer-jobs">Careers</Link>
              <a href="tel:+18008241906" className="footer-link" data-testid="footer-call">Call Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default JobPhilippines;
